import type moment from 'moment'

import type { Shift } from 'modules/shift_management/lib/types'

export type MinimumRestViolation = {
  shiftId: number
  employeeId: number
  violationDay: moment.Moment
  requiredMinutes: number
  actualMinutes: number
}

const minutesBetween = (end: moment.Moment, start: moment.Moment): number => {
  return start.diff(end, 'minutes')
}

const getDayKey = (date: moment.Moment): string => date.format('YYYY-MM-DD')

const getLastShiftByDay = (shifts: Shift[]): Map<string, Shift> => {
  const map = new Map<string, Shift>()
  shifts.forEach((shift) => {
    const dayKey = getDayKey(shift.currentStartDate)
    const existing = map.get(dayKey)
    if (!existing || shift.currentEndDate.isAfter(existing.currentEndDate)) {
      map.set(dayKey, shift)
    }
  })
  return map
}

const getFirstShiftByDay = (shifts: Shift[]): Map<string, Shift> => {
  const map = new Map<string, Shift>()
  shifts.forEach((shift) => {
    const dayKey = getDayKey(shift.currentStartDate)
    const existing = map.get(dayKey)
    if (!existing || shift.currentStartDate.isBefore(existing.currentStartDate)) {
      map.set(dayKey, shift)
    }
  })
  return map
}

export const minimumRestViolationsBetweenDays = (
  shifts: Shift[],
  requiredMinutes: number
): MinimumRestViolation[] => {
  if (!requiredMinutes || requiredMinutes <= 0) return []

  const lastByDay = getLastShiftByDay(shifts)
  const firstByDay = getFirstShiftByDay(shifts)
  const violations: MinimumRestViolation[] = []

  const dayKeys = Array.from(new Set([...lastByDay.keys(), ...firstByDay.keys()])).sort()

  dayKeys.forEach((currentDay) => {
    const lastShift = lastByDay.get(currentDay)
    if (!lastShift) return

    const nextDayKey = lastShift.currentStartDate
      .clone()
      .startOf('day')
      .add(1, 'day')
      .format('YYYY-MM-DD')
    const firstShift = firstByDay.get(nextDayKey)
    if (!firstShift) return

    const actualMinutes = minutesBetween(lastShift.currentEndDate, firstShift.currentStartDate)
    if (actualMinutes < requiredMinutes) {
      violations.push({
        shiftId: firstShift.id,
        employeeId: firstShift.employeeId,
        violationDay: firstShift.currentStartDate.clone().startOf('day'),
        requiredMinutes,
        actualMinutes,
      })
    }
  })

  return violations
}
